import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import {
  AdditiveBlending,
  Color,
  IcosahedronGeometry,
  type BufferGeometry,
  type Group,
  type Mesh,
  type Sprite,
  type Texture,
  Vector3,
} from 'three';
import { dueLabel, taskWeight, type Task } from '@map/shared';
import { BELT_BOB, BELT_HEIGHT, BELT_INNER, BELT_SPREAD, FIELD_OF_VIEW } from './framing.js';
import { glowTexture } from './planetSkin.js';
import { sceneColor } from '../styles/tokens.js';

/**
 * Work still to do, in the sky.
 *
 * Every open task is a rock: irregular, lit, and sized by how much it matters
 * rather than how long it takes. A rock names itself the moment you point at
 * it, because it wants dealing with.
 *
 * Nothing finished is up here. A completed task leaves the sky altogether and
 * becomes a boat on the ocean of the planet it was closest to (globe/Boats),
 * so the system view only ever shows what is still outstanding.
 */
export interface FieldHover {
  task: Task;
  x: number;
  y: number;
}

export interface TaskFieldProps {
  tasks: Task[];
  /** The outermost planet orbit: the field is laid out relative to the system. */
  span: number;
  still: boolean;
  hoveredId: string | null;
  onHover: (hover: FieldHover | null) => void;
  onOpen?: ((task: Task) => void) | undefined;
  /** A phone frames the system from further back: the field draws in to suit. */
  compact?: boolean;
  /**
   * Where each planet is this frame, and how far out its rings and moons
   * reach. Rocks keep off them on screen, so a task never hides a world.
   */
  obstacles?: MutableRefObject<Map<string, Obstacle>> | undefined;
}

/** Something on screen a rock must not cover: a world-space centre and reach. */
export interface Obstacle {
  x: number;
  y: number;
  z: number;
  r: number;
}

/**
 * A P1 wanted ASAP. These are not one rock among many: they are drawn bright
 * red at five times the area of what they would otherwise be, so they are
 * the first thing the eye lands on.
 */
export function isAlarm(task: Task): boolean {
  return task.importance === 'p1' && (task.urgency === 'urgent' || task.urgency === 'critical');
}
const ALARM_SCALE = Math.sqrt(5);

/** How big a rock is drawn, in world units. */
function rockSize(task: Task): number {
  // Importance sets the size and pressure only escalates it — see taskWeight.
  const size = 0.3 + taskWeight(task).rating * 0.66;
  return isAlarm(task) ? size * ALARM_SCALE : size;
}

/** Where a rock would be if nothing else were in the sky. */
interface Orbit {
  radius: number;
  height: number;
  speed: number;
  tilt: number;
  phase: number;
  /** How far the gentle hover carries it up and down. */
  bob: number;
}

function orbitOf(task: Task, span: number, compact: boolean): Orbit {
  const b = hash(task.id, 2);
  const c = hash(task.id, 3);
  /**
   * The belt sits just outside the last planet's orbit and no further. Far
   * enough out to be plainly not a planet, close enough in that the whole of
   * it is on screen at the default framing — worked out against the camera's
   * field of view rather than guessed, since a rock that drifts off the edge
   * of the window is a task you have been told about and cannot see.
   */
  const radius = span * (BELT_INNER - (compact ? 0.01 : 0) + b * BELT_SPREAD);
  return {
    radius,
    height: (c - 0.5) * span * BELT_HEIGHT,
    // A slow drift rather than a lap: about a quarter of an hour to come
    // round, so the belt reads as hanging there rather than running on a track.
    speed: 0.025 / Math.sqrt(Math.max(0.5, radius)),
    tilt: (hash(task.id, 1) - 0.5) * 0.28,
    phase: hash(task.id, 8) * 4,
    bob: span * BELT_BOB,
  };
}

/** Stable per-task randomness: the same rock, in the same place, every load. */
function hash(id: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

/**
 * A rock, not a ball.
 *
 * An icosahedron's vertices are pushed in and out by a value noise read from
 * the direction they point in. Sampling by direction rather than per vertex
 * matters: the geometry is non-indexed, so the same corner appears once per
 * face, and displacing those copies by different amounts would split the rock
 * into confetti. Quantising the direction first makes every copy agree.
 */
function rockGeometry(seed: number, roughness: number): BufferGeometry {
  const geometry = new IcosahedronGeometry(1, 2);
  const position = geometry.attributes.position;
  if (!position) return geometry;

  const noise = (x: number, y: number, z: number) => {
    let h = seed ^ 0x9e3779b9;
    h = Math.imul(h ^ Math.round(x * 24), 0x85ebca6b);
    h = Math.imul(h ^ Math.round(y * 24), 0xc2b2ae35);
    h = Math.imul(h ^ Math.round(z * 24), 0x27d4eb2f);
    h ^= h >>> 15;
    return ((h >>> 0) % 10000) / 10000;
  };

  for (let i = 0; i < position.count; i += 1) {
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);
    const length = Math.hypot(x, y, z) || 1;
    const nx = x / length;
    const ny = y / length;
    const nz = z / length;
    // Two octaves: one that dents whole sides, one that chips the edges.
    const broad = noise(nx, ny, nz) - 0.5;
    const fine = noise(nx * 2.7, ny * 2.7, nz * 2.7) - 0.5;
    const radius = 1 + (broad * 0.9 + fine * 0.35) * roughness;
    position.setXYZ(i, nx * radius, ny * radius, nz * radius);
  }

  position.needsUpdate = true;
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * How loud a rock's air is, and how hard it beats.
 *
 * A steady glow is scenery — the eye files it with the starfield inside a
 * second and never reports it again. What the eye cannot file is a change, so
 * every rock beats: a sharp flare and a decay, like a beacon rather than a
 * sine wave breathing. Calm work beats slowly and softly and an overdue P1
 * beats fast and hard, so the sky has a pulse rate you can read across the
 * room without reading a single word.
 */
function auraOf(heat: number, alarm = false): { colour: string; base: number; swing: number; period: number } {
  if (alarm) return { colour: sceneColor('debris-alarm', '#ff2d2d'), base: 0.6, swing: 0.7, period: 1.1 };
  if (heat >= 0.62)
    return { colour: sceneColor('health-4', '#e05c3e'), base: 0.4, swing: 0.6, period: 1.6 - heat * 0.7 };
  if (heat >= 0.3)
    return { colour: sceneColor('health-2', '#e3c04a'), base: 0.28, swing: 0.42, period: 2.6 - heat * 1.2 };
  return { colour: sceneColor('debris-open', '#dfe2e7'), base: 0.14, swing: 0.22, period: 3.4 - heat * 1.2 };
}

/**
 * One beat: an instant flare that falls away, not a sine. `phase` runs 0..1
 * over the period, so the flare lands on the beat and the rock is dark again
 * well before the next one — which is what makes it read as a signal.
 */
function beatAt(time: number, period: number): number {
  const phase = (time % period) / period;
  return Math.exp(-phase * 5.5) + 0.35 * Math.exp(-((phase - 0.22) ** 2) * 220);
}

function Rock({
  task,
  still,
  hovered,
  onHover,
  onOpen,
  glow,
  compact,
  place,
}: {
  task: Task;
  still: boolean;
  hovered: boolean;
  onHover: (hover: FieldHover | null) => void;
  onOpen?: ((task: Task) => void) | undefined;
  glow: Texture | null;
  compact: boolean;
  /** Hands the rock's group to the field, which decides where it sits. */
  place: (id: string, group: Group | null) => void;
}) {
  const { camera } = useThree();
  const body = useRef<Mesh>(null);
  const halo = useRef<Sprite>(null);

  const a = hash(task.id, 1);
  const b = hash(task.id, 2);
  const c = hash(task.id, 3);
  const weight = useMemo(() => taskWeight(task), [task]);
  const alarm = isAlarm(task);
  const size = useMemo(() => rockSize(task), [task]);

  const geometry = useMemo(() => rockGeometry(Math.round(a * 1e6), 0.42 + b * 0.3), [a, b]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const colour = useMemo(
    () => new Color(alarm ? sceneColor('debris-alarm', '#ff2d2d') : sceneColor('debris-open', '#dfe2e7')),
    [alarm],
  );
  // An alarm is always at full heat: it is the loudest thing in the sky.
  const aura = useMemo(() => auraOf(alarm ? 1 : weight.heat, alarm), [weight.heat, alarm]);
  const auraColour = useMemo(() => new Color(aura.colour), [aura.colour]);

  // Its own place in the beat, so a sky of rocks never flashes in unison.
  const phase = hash(task.id, 8) * 4;
  const heat = alarm ? 1 : weight.heat;

  const setGroup = useCallback((group: Group | null) => place(task.id, group), [place, task.id]);

  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;
    if (!still && body.current) {
      body.current.rotation.x += delta * (0.05 + a * 0.1);
      body.current.rotation.y += delta * (0.04 + b * 0.09);
      body.current.rotation.z += delta * (0.03 + c * 0.07);
    }

    // Reduced motion gets the brightness without the flashing: the signal is
    // still legible, it just does not move.
    const beat = still ? 0.5 : beatAt(time + phase, aura.period);

    if (body.current) {
      const material = body.current.material as unknown as { emissiveIntensity: number };
      material.emissiveIntensity = (alarm ? 0.6 : hovered ? 0.45 : 0.28) + beat * (0.3 + heat * 0.95);
      // The rock itself swells a little on the beat, so the signal survives
      // even where the halo is washed out against something bright.
      const swell = 1 + beat * 0.05 * (0.4 + heat);
      body.current.scale.setScalar(size * (hovered ? 1.12 : 1) * swell);
    }

    if (halo.current) {
      // Held against the camera as well as the rock, so pulling out of the
      // system never shrinks the one thing saying a task is pressing.
      const unit = Math.max(size * 1.5, camera.position.length() * 0.02);
      const across = unit * (hovered ? 3.4 : 2.6) * (1 + beat * 0.55);
      halo.current.scale.set(across, across, 1);
      (halo.current.material as { opacity: number }).opacity = Math.min(
        1,
        aura.base + beat * aura.swing + (hovered ? 0.15 : 0),
      );
    }
  });

  const point = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    onHover({ task, x: e.clientX, y: e.clientY });
  };

  return (
    <group ref={setGroup}>
      <mesh
        ref={body}
        geometry={geometry}
        scale={size}
        onPointerMove={point}
        onPointerOver={point}
        onPointerOut={() => onHover(null)}
        onClick={(e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          onOpen?.(task);
        }}
      >
        <meshStandardMaterial
          flatShading
          color={colour}
          roughness={1}
          metalness={0}
          emissive={colour}
          emissiveIntensity={0.34}
        />
      </mesh>

      {/* Its air: what the task is asking of you right now, which is not the
          same fact as how big it is. */}
      {glow && (
        <sprite ref={halo} renderOrder={1}>
          <spriteMaterial
            map={glow}
            color={auraColour}
            transparent
            opacity={aura.base}
            depthWrite={false}
            blending={AdditiveBlending}
          />
        </sprite>
      )}

      {/* A rock is a few pixels across on a phone and a fingertip is not. Only
          there: on a desktop a hit area wider than the rock would steal
          clicks meant for the planet beside it. */}
      {compact && (
        <mesh
          scale={size * 1.6}
          onPointerMove={point}
          onPointerOut={() => onHover(null)}
          onClick={(e: ThreeEvent<MouseEvent>) => {
            e.stopPropagation();
            onOpen?.(task);
          }}
        >
          <sphereGeometry args={[1, 8, 8]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      )}
    </group>
  );
}

/** A rock's state in the layout: where its orbit puts it, and how far it has been nudged. */
interface Slot {
  group: Group | null;
  orbit: Orbit;
  angle: number;
  /** World-space nudge away from its orbit, held between frames and eased home. */
  offset: Vector3;
  /** Body reach in world units: the noise pushes corners out to ~1.3× the size. */
  reach: number;
}

/** Room on screen, in pixels, kept between two things. */
const GAP_PX = 6;
/** Bands of the window the header and the planet strip already use. */
const MARGIN = { top: 72, bottom: 104, side: 16 };

/**
 * Keeps the sky readable.
 *
 * Every rock has an orbit, but where it is drawn is decided here, on screen:
 * each frame the rocks are projected, and any two that overlap — or any rock
 * that sits over a planet, its rings or its moons — are pushed apart sideways
 * to the view, in pixels, then the push is carried back into the world at
 * that rock's depth. The nudge is kept from frame to frame and eases back
 * toward the orbit when nothing is in the way, so rocks part to let a planet
 * through and then drift home rather than jumping. Doing this on screen
 * rather than in space is the point: two rocks far apart in depth still hide
 * each other if they line up from where you are looking.
 */
function useLayout(
  slots: MutableRefObject<Map<string, Slot>>,
  still: boolean,
  obstacles: MutableRefObject<Map<string, Obstacle>> | undefined,
) {
  const { camera, size } = useThree();
  const scratch = useMemo(
    () => ({ v: new Vector3(), right: new Vector3(), up: new Vector3(), tilt: new Vector3() }),
    [],
  );

  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;
    const step = Math.min(delta, 0.1);
    const focal = size.height / 2 / Math.tan((FIELD_OF_VIEW * Math.PI) / 360);
    camera.updateMatrixWorld();
    const view = camera.matrixWorldInverse;
    scratch.right.setFromMatrixColumn(camera.matrixWorld, 0);
    scratch.up.setFromMatrixColumn(camera.matrixWorld, 1);

    const list = [...slots.current.values()].filter((s) => s.group);
    const n = list.length;
    // Screen position (px from the centre, y up), radius in px, and depth.
    const sx = new Float32Array(n);
    const sy = new Float32Array(n);
    const sr = new Float32Array(n);
    const depth = new Float32Array(n);
    const base: Vector3[] = [];

    for (const slot of list) {
      const { orbit } = slot;
      if (!still) slot.angle += step * orbit.speed;
      // A gentle hover on top of the drift: slow, shallow, and out of step with
      // every other rock so the belt never bobs as one body.
      const bob = still ? 0 : Math.sin(time * 0.18 + orbit.phase) * orbit.bob;
      const point = new Vector3(
        Math.cos(slot.angle) * orbit.radius,
        orbit.height + bob,
        Math.sin(slot.angle) * orbit.radius,
      );
      // The orbit's own tilt, as the group used to carry it: x then z.
      point.applyAxisAngle(scratch.tilt.set(0, 0, 1), orbit.tilt * 0.6);
      point.applyAxisAngle(scratch.tilt.set(1, 0, 0), orbit.tilt);
      base.push(point);
      // Ease the nudge home: a few seconds to drift back once the way is clear.
      slot.offset.multiplyScalar(Math.exp(-step * 0.5));
    }

    const project = () => {
      for (let i = 0; i < n; i += 1) {
        const slot = list[i] as Slot;
        scratch.v.copy(base[i] as Vector3).add(slot.offset).applyMatrix4(view);
        const d = Math.max(0.01, -scratch.v.z);
        depth[i] = d;
        sx[i] = (scratch.v.x * focal) / d;
        sy[i] = (scratch.v.y * focal) / d;
        sr[i] = (slot.reach * focal) / d + GAP_PX / 2;
      }
    };

    // Planets, projected once: they do not move for rocks.
    const walls: Array<{ x: number; y: number; r: number }> = [];
    for (const o of obstacles?.current.values() ?? []) {
      scratch.v.set(o.x, o.y, o.z).applyMatrix4(view);
      const d = -scratch.v.z;
      if (d <= 0.01) continue;
      walls.push({ x: (scratch.v.x * focal) / d, y: (scratch.v.y * focal) / d, r: (o.r * focal) / d + GAP_PX });
    }

    const halfW = size.width / 2;
    const halfH = size.height / 2;
    const push = (i: number, px: number, py: number) => {
      const slot = list[i] as Slot;
      const scale = (depth[i] as number) / focal;
      slot.offset.addScaledVector(scratch.right, px * scale).addScaledVector(scratch.up, py * scale);
    };

    for (let pass = 0; pass < 6; pass += 1) {
      project();
      for (let i = 0; i < n; i += 1) {
        for (let j = i + 1; j < n; j += 1) {
          const dx = (sx[j] as number) - (sx[i] as number);
          const dy = (sy[j] as number) - (sy[i] as number);
          const want = (sr[i] as number) + (sr[j] as number);
          const dist = Math.hypot(dx, dy);
          if (dist >= want) continue;
          // Two in exactly the same spot part along a fixed, arbitrary line.
          const ux = dist > 1e-3 ? dx / dist : 1;
          const uy = dist > 1e-3 ? dy / dist : 0;
          const overlap = want - dist;
          // The smaller rock gives way more: a big red one holds its ground.
          const ri = sr[i] as number;
          const rj = sr[j] as number;
          const wi = rj / (ri + rj);
          push(i, -ux * overlap * wi, -uy * overlap * wi);
          push(j, ux * overlap * (1 - wi), uy * overlap * (1 - wi));
        }
        for (const wall of walls) {
          const dx = (sx[i] as number) - wall.x;
          const dy = (sy[i] as number) - wall.y;
          const want = (sr[i] as number) + wall.r;
          const dist = Math.hypot(dx, dy);
          if (dist >= want) continue;
          const ux = dist > 1e-3 ? dx / dist : 0;
          const uy = dist > 1e-3 ? dy / dist : 1;
          push(i, ux * (want - dist), uy * (want - dist));
        }
        // Kept inside the window, clear of the header and the planet strip,
        // so a rock pushed aside is never pushed out of sight.
        const r = sr[i] as number;
        const x = sx[i] as number;
        const y = sy[i] as number;
        const left = -halfW + MARGIN.side + r;
        const right = halfW - MARGIN.side - r;
        const top = halfH - MARGIN.top - r;
        const bottom = -halfH + MARGIN.bottom + r;
        let px = 0;
        let py = 0;
        if (left < right) px = x < left ? left - x : x > right ? right - x : 0;
        if (bottom < top) py = y < bottom ? bottom - y : y > top ? top - y : 0;
        if (px || py) push(i, px, py);
      }
    }

    for (let i = 0; i < n; i += 1) {
      const slot = list[i] as Slot;
      slot.group?.position.copy(base[i] as Vector3).add(slot.offset);
    }
  });
}

export function TaskField({
  tasks,
  span,
  still,
  hoveredId,
  onHover,
  onOpen,
  compact = false,
  obstacles,
}: TaskFieldProps) {
  // One texture for the whole sky: a halo per task would be a canvas and a GPU
  // allocation each.
  const glow = useMemo(() => glowTexture(), []);
  useEffect(() => () => glow?.dispose(), [glow]);

  const slots = useRef(new Map<string, Slot>());
  // Kept across filtering, so a rock hidden by a search and shown again comes
  // back where it was rather than at its starting angle.
  const angles = useRef(new Map<string, number>());

  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const byIdRef = useRef(byId);
  byIdRef.current = byId;

  const place = useCallback(
    (id: string, group: Group | null) => {
      const existing = slots.current.get(id);
      if (!group) {
        if (existing) angles.current.set(id, existing.angle);
        slots.current.delete(id);
        return;
      }
      const task = byIdRef.current.get(id);
      if (!task) return;
      slots.current.set(id, {
        group,
        orbit: orbitOf(task, span, compact),
        angle: angles.current.get(id) ?? hash(id, 4) * Math.PI * 2,
        offset: existing?.offset ?? new Vector3(),
        reach: rockSize(task) * 1.3,
      });
    },
    [span, compact],
  );

  // An edit can change a rock's size (P1, ASAP) without remounting it.
  useEffect(() => {
    for (const [id, slot] of slots.current) {
      const task = byId.get(id);
      if (task) slot.reach = rockSize(task) * 1.3;
    }
  }, [byId]);

  useLayout(slots, still, obstacles);

  return (
    <group>
      {tasks.map((task) => (
        <Rock
          key={task.id}
          task={task}
          still={still}
          hovered={hoveredId === task.id}
          onHover={onHover}
          onOpen={onOpen}
          glow={glow}
          compact={compact}
          place={place}
        />
      ))}
    </group>
  );
}

/** dd M YYYY — 07 Feb 2026. The one date format the sky uses. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function skyDate(iso: string): string {
  // A civil date (yyyy-mm-dd) would be read as UTC midnight and shift a day
  // west of Greenwich; a timestamp is already absolute.
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** What it is: under the pointer for a rock, after a pause for a star. */
export function FieldCard({ hover, mobile = false }: { hover: FieldHover | null; mobile?: boolean }) {
  if (!hover) return null;
  // The due date, in cyan, or the words for not having one — never a blank.
  const line = dueLabel(hover.task);

  if (mobile) {
    return (
      <div
        className="pointer-events-none fixed inset-x-3 z-40 rounded-[var(--radius-md)] px-4 py-3"
        style={{
          bottom: 'calc(var(--safe-bottom) + 9.75rem)',
          background: 'color-mix(in oklch, var(--surface-1) 94%, transparent)',
          boxShadow: 'var(--shadow-panel)',
        }}
      >
        <div className="text-base leading-snug">{hover.task.title}</div>
        <div className="mt-1 text-sm" style={{ color: 'var(--debris-date)' }}>
          {line}
        </div>
      </div>
    );
  }

  // Viewport coordinates, so the card is fixed rather than placed in the
  // canvas's box; flipped near the right edge so it never runs off screen.
  const flip = hover.x > window.innerWidth - 280;
  return (
    <div
      className="pointer-events-none fixed z-30 max-w-xs rounded-[var(--radius-md)] px-3 py-2 text-sm"
      style={{
        left: hover.x + (flip ? -14 : 14),
        top: hover.y + 14,
        transform: flip ? 'translateX(-100%)' : undefined,
        background: 'color-mix(in oklch, var(--surface-1) 92%, transparent)',
        boxShadow: 'var(--shadow-raised)',
      }}
    >
      <div className="leading-snug">{hover.task.title}</div>
      <div className="mt-0.5 text-xs" style={{ color: 'var(--debris-date)' }}>
        {line}
      </div>
    </div>
  );
}
